import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ButtonsComponent } from '../../../shared/buttons/buttons.component';
import { InputComponent } from '../../../shared/input/input.component';
import * as PopupConstants from '../../../shared/constants/popup-constants';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { SearchableMultiSelectComponent } from '../../../shared/searchable-multi-select/searchable-multi-select.component';
import { ShareItemsService } from '../../../services/share-feature/share-items.service';
import { forkJoin, lastValueFrom } from 'rxjs';
import { noWhitespaceValidator } from '../../../shared/validators/no-whitespace.validator';
import { PanelGroupNormalized, PanelTableData } from '../../../interfaces/sv-test-pannel';

/** Dialog payload passed via MAT_DIALOG_DATA when opening the svtest edit modal. */
export interface SvtestEditModalData {
  svTestData: PanelTableData;
}

@Component({
  selector: 'app-svtest-edit-modal',
  standalone: true,
  imports: [
    InputComponent,
    ButtonsComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    CommonModule,
    SearchableMultiSelectComponent,
  ],
  templateUrl: './svtest-edit-modal.component.html',
  styleUrl: './svtest-edit-modal.component.css',
})
export class SvtestEditModalComponent implements OnInit {
  sampleEditForm = new FormGroup({
    Name: new FormControl('', [Validators.required, Validators.maxLength(256), noWhitespaceValidator()]),
    Description: new FormControl('', [Validators.maxLength(1024), noWhitespaceValidator()]),
    groups: new FormControl<string[]>([]),
  });
  fullGroupsList: PanelGroupNormalized[] = [];
  groupOptions: string[] = [];
  initialSecondaryGroupNames: string[] = [];
  disabledGroupOptions: string[] = [];
  primaryGroup: PanelGroupNormalized | null = null;
  isSubmitting = false;
  buttonCancel = PopupConstants.BUTTON_CANCEL;
  arialLabel = PopupConstants.ARIAL_LABEL;
  btnMat = PopupConstants.BTN_MAT;
  btnStyleText = PopupConstants.BTN_STYLE_TEXT;

  buttonSave = PopupConstants.BUTTON_SAVE;
  btnMatFlat = PopupConstants.BTN_MAT_FLAT;
  btnStyleFilledSmall = PopupConstants.BTN_STYLE_FILLED_SMALL;
  btnStyleFilledSmallDisabled = PopupConstants.BTN_STYLE_FILLED_SMALL_DISABLED;

  constructor(
    public dialogRef: MatDialogRef<SvtestEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SvtestEditModalData,
    private shareItemsService: ShareItemsService
  ) {
    this.sampleEditForm.get('Name')?.setValue(data.svTestData.name ? data.svTestData.name : '');
    this.sampleEditForm.get('Description')?.setValue(data.svTestData.description ? data.svTestData.description : '');
  }

  ngOnInit(): void {
    this.initializeGroupControl();
  }

  initializeGroupControl() {
    this.fullGroupsList = this.data.svTestData.groups ?? [];
    this.primaryGroup = this.fullGroupsList.find((g) => g.isPrimary) ?? null;

    this.groupOptions = this.fullGroupsList.map((g) => g.groupName);

    this.initialSecondaryGroupNames = this.fullGroupsList.map((g) => g.groupName);
    this.sampleEditForm.get('groups')?.setValue(this.initialSecondaryGroupNames);
    if (this.primaryGroup) {
      this.disabledGroupOptions = [this.primaryGroup.groupName];
    }
  }

  closePopup() {
    this.dialogRef.close({
      data: this.data.svTestData,
      event: 'EDIT',
    });
  }

  getChangedFields(formGroup: FormGroup): any {
    const changedFields: any = {};
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control?.dirty) {
        changedFields[key] = control.value;
      }
    });
    return changedFields;
  }

  async onEditSubmit() {
    if (this.sampleEditForm.valid) {
      this.isSubmitting = true;
      const currentSelection = this.sampleEditForm.get('groups')?.value || [];
      const removedGroupNames = this.initialSecondaryGroupNames.filter(
        (name: string) => !currentSelection.includes(name)
      );

      const apiCalls: any = [];
      const panelId = this.data.svTestData.panelId;

      if (removedGroupNames.length > 0) {
        const removedGroupIds = this.fullGroupsList
          .filter((g) => removedGroupNames.includes(g.groupName))
          .map((g) => g.groupId);

        removedGroupIds.forEach((groupId) => {
          apiCalls.push(this.shareItemsService.removeResourceFromGroup('panels', panelId, groupId));
        });
      }

      if (apiCalls.length > 0) {
        try {
          await lastValueFrom(forkJoin(apiCalls));
        } catch (error) {
          console.error('Error removing groups', error);
        }
      }

      const updatedFields: any = {};
      const nameControl = this.sampleEditForm.get('Name');
      const descControl = this.sampleEditForm.get('Description');

      if (nameControl?.dirty) updatedFields['Name'] = nameControl.value;
      if (descControl?.dirty) updatedFields['Description'] = descControl.value;

      const hasMetadataChanges = Object.keys(updatedFields).length > 0;
      const hasGroupChanges = removedGroupNames.length > 0;

      if (hasMetadataChanges) {
        this.dialogRef.close({
          data: updatedFields,
          panelId: panelId,
          event: 'SUBMIT',
        });
      } else if (hasGroupChanges) {
        this.dialogRef.close({
          event: 'RELOAD',
        });
      } else {
        this.closePopup();
      }
    }
  }
}
